import { KnownUpdateSettingsSchemaDtoKind } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useMemo } from 'react';
import { useQueryGetProjectChildProjects } from '../../../../core/queries/projects.queries';
import {
  useMutationUpdateRegistrationPage,
  useQueryGetRegistrationPageOptional
} from '../../../registration-pages/queries';
import { useQueryListSettingsSchemas } from '../../../settings-schemas/queries';
import { EntityForm } from '../form.types';
import { RegistrationPageForm } from './form';
import { RegistrationPageFormOutput } from './form-output';
import { RegistrationPageFormSchema } from './form-schema';
import { registrationPageFormUISchema } from './ui-schema';

export function useEditRegistrationPageForm(
  projectID: string | undefined,
  registrationPageID: string | undefined
): EntityForm | null {
  const registrationPage = useQueryGetRegistrationPageOptional(projectID, registrationPageID);
  const registrationPageMutation = useMutationUpdateRegistrationPage();
  const childProjects = useQueryGetProjectChildProjects(projectID);
  const settingsSchemas = useQueryListSettingsSchemas();

  const childProjectData = useMemo(() => {
    return (childProjects.data || []).filter((p) => p.id !== projectID);
  }, [childProjects.data, projectID]);

  const registrationPageSchema = useMemo(() => {
    return settingsSchemas.data?.find(
      (schema) => schema.kind === KnownUpdateSettingsSchemaDtoKind.RegistrationPage && schema.name === 'default'
    );
  }, [settingsSchemas.data]);

  const defaultValues = useMemo(() => {
    return RegistrationPageForm.GetInitialData(registrationPage.data?.data);
  }, [registrationPage.data]);

  const initialFormOutput = useMemo(() => {
    return new RegistrationPageFormOutput(defaultValues);
  }, [defaultValues]);

  const formSchema = useMemo(() => {
    return RegistrationPageFormSchema.FromSettingsSchema(childProjectData, 'edit', registrationPageSchema);
  }, [childProjectData, registrationPageSchema]);

  const onSubmit = useCallback(
    async (formOutput: RegistrationPageFormOutput) => {
      if (!projectID || !registrationPageID) {
        return;
      }
      await registrationPageMutation.mutateAsync({
        projectID,
        registrationPageID: registrationPageID,
        settings: RegistrationPageFormOutput.ToEditSettingsDto(formOutput)
      });
      await registrationPage.refetch();
      return {
        entityId: registrationPageID,
        parentEntityId: projectID
      };
    },
    [registrationPageID, projectID, registrationPage, registrationPageMutation]
  );

  if (!projectID) {
    return null;
  }

  if (!formSchema) {
    return null;
  }

  return {
    // TO DO: Once we are fetching the Project List, add its loading state to the isLoading check
    isLoading: registrationPage.isLoading || childProjects.isLoading || settingsSchemas.isLoading,
    title: 'Registration Page Settings',
    defaultValues,
    initialFormOutput: initialFormOutput,
    formSchema,
    uiSchema: registrationPageFormUISchema,
    getOutput(formData) {
      return new RegistrationPageFormOutput(formData as RegistrationPageForm);
    },
    onSubmit,
    isLocked: false,
    lockedMessage: null
  };
}
